import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, MapPin, Star, Mail, Phone, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDashboardPage({ adminUser, onAdminLogout }) {
  const [resorts, setResorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    byType: {},
    byState: {}
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!adminUser) {
      navigate('/admin/login');
      return;
    }
    fetchResorts();
  }, [adminUser, navigate]);

  const fetchResorts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/resorts`);
      const data = await response.json();
      setResorts(data);
      
      // Calculate stats
      const byType = {};
      const byState = {};
      data.forEach(resort => {
        byType[resort.type] = (byType[resort.type] || 0) + 1;
        byState[resort.state] = (byState[resort.state] || 0) + 1;
      });
      
      setStats({
        total: data.length,
        byType,
        byState
      });
    } catch (error) {
      console.error('Error fetching resorts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onAdminLogout();
    navigate('/admin/login');
  };

  if (!adminUser) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage resorts and platform settings</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Logged in as</p>
                <p className="font-semibold text-gray-900">{adminUser.name}</p>
              </div>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Resorts</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Property Types</p>
                  <p className="text-3xl font-bold text-gray-900">{Object.keys(stats.byType).length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">States Covered</p>
                  <p className="text-3xl font-bold text-gray-900">{Object.keys(stats.byState).length}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Resorts List */}
        <Card>
          <CardHeader>
            <CardTitle>All Resorts ({resorts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-600">Loading resorts...</p>
            ) : resorts.length === 0 ? (
              <p className="text-center py-8 text-gray-600">No resorts found</p>
            ) : (
              <div className="space-y-4">
                {resorts.map((resort) => (
                  <Card key={resort.id} className="border">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <h3 className="text-xl font-bold text-gray-900">{resort.name}</h3>
                            <Badge className="bg-emerald-100 text-emerald-800">
                              {resort.type}
                            </Badge>
                            <div className="flex items-center text-amber-500">
                              <Star className="h-4 w-4 fill-current" />
                              <span className="ml-1 font-semibold">{resort.rating}</span>
                            </div>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div className="space-y-2">
                              <p className="text-gray-600">
                                <MapPin className="h-4 w-4 inline mr-2" />
                                {resort.location}, {resort.state}
                              </p>
                              {resort.email && (
                                <p className="text-gray-600">
                                  <Mail className="h-4 w-4 inline mr-2" />
                                  {resort.email}
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <p className="text-gray-600">
                                <span className="font-semibold">Price:</span> ₹{resort.price}/night
                              </p>
                              {resort.phone && (
                                <p className="text-gray-600">
                                  <Phone className="h-4 w-4 inline mr-2" />
                                  {resort.phone}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {resort.description && (
                            <p className="text-gray-600 mt-3 text-sm">{resort.description}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminDashboardPage;
